import React, {useRef} from "react";
import {
  Card,
  Form,
  Button,
  Image,
  Row,
  Col,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  FaImage,
  FaVideo,
  FaPoll,
  FaLink,
  FaGift,
  FaClock,
  FaCalendarAlt,
  FaWhatsapp,
  FaEdit,
  FaFilePdf,
} from "react-icons/fa";

const CommunicationEditor = ({
  comunicado,
  index,
  onUpdate,
  incentives = [],
  onShowPollModal,
  onShowLinkModal,
  totalComunicados = 1,
}) => {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);

  // Función para actualizar el comunicado
  const updateField = (field, value) => {
    onUpdate({[field]: value});
  };

  // Funciones para manejo de archivos
  const handleImageUpload = () => {
    imageInputRef.current?.click();
  };

  const handleVideoUpload = () => {
    videoInputRef.current?.click();
  };

  const handleDocumentUpload = () => {
    documentInputRef.current?.click();
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      updateField("imagePreview", imageUrl);
      updateField("videoPreview", null); // Limpiar video si se carga imagen
    }
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const videoUrl = URL.createObjectURL(file);
      updateField("videoPreview", videoUrl);
      updateField("imagePreview", null); // Limpiar imagen si se carga video
    }
  };

  const handleDocumentFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      updateField("documentPreview", {
        name: file.name,
        url: URL.createObjectURL(file),
        size: file.size,
      });
    }
  };

  // Función para calcular cuándo se enviará este comunicado
  const calculateSendTime = () => {
    if (index === 0) return "Inmediatamente";

    const days = comunicado.delayDays || 0;
    const hours = comunicado.delayHours || 0;

    if (days === 0 && hours === 0) return "Inmediatamente después del anterior";

    let timeText = "";
    if (days > 0) timeText += `${days} día${days > 1 ? "s" : ""}`;
    if (hours > 0) {
      if (timeText) timeText += " y ";
      timeText += `${hours} hora${hours > 1 ? "s" : ""}`;
    }

    return `${timeText} después del anterior`;
  };

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <Badge bg="primary" pill className="me-2">
            {index + 1}
          </Badge>
          <h6 className="mb-0 fw-bold">
            <FaEdit className="me-2" />
            {comunicado.title || `Comunicado ${index + 1}`}
          </h6>
        </div>
        <small className="text-muted">
          <FaClock className="me-1" />
          {calculateSendTime()}
        </small>
      </Card.Header>

      <Card.Body>
        <Row>
          {/* Columna de Edición */}
          <Col md={8}>
            {/* Título del Comunicado */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">
                Título del Comunicado
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={`Ej: Comunicado ${
                  index + 1
                } - Información importante`}
                value={comunicado.title || ""}
                onChange={(e) => updateField("title", e.target.value)}
              />
            </Form.Group>

            {/* Timing (solo para comunicados después del primero) */}
            {index > 0 && (
              <Card className="mb-3 bg-light">
                <Card.Body className="p-3">
                  <Form.Label className="fw-bold small mb-2">
                    <FaCalendarAlt className="me-2" />
                    Programación de Envío
                  </Form.Label>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">
                          Días de espera
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="365"
                          value={comunicado.delayDays || 0}
                          onChange={(e) =>
                            updateField(
                              "delayDays",
                              parseInt(e.target.value) || 0
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">
                          Horas adicionales
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="23"
                          value={comunicado.delayHours || 0}
                          onChange={(e) =>
                            updateField(
                              "delayHours",
                              parseInt(e.target.value) || 0
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Alert variant="info" className="small mb-0 mt-2">
                    <strong>Este comunicado se enviará:</strong>{" "}
                    {calculateSendTime()}
                  </Alert>
                </Card.Body>
              </Card>
            )}

            {/* Mensaje de Texto */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">
                Mensaje de Texto
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="Escribe el contenido de este comunicado..."
                value={comunicado.message || ""}
                onChange={(e) => updateField("message", e.target.value)}
              />
              <Form.Text className="text-muted">
                {comunicado.message?.length || 0} caracteres
              </Form.Text>
            </Form.Group>

            {/* Elementos Multimedia */}
            <div className="mb-3">
              <Form.Label className="fw-bold small mb-2">
                Elementos Multimedia (Opcional)
              </Form.Label>

              {/* Inputs ocultos para archivos */}
              <input
                type="file"
                ref={imageInputRef}
                style={{display: "none"}}
                accept="image/*"
                onChange={handleImageFileChange}
              />
              <input
                type="file"
                ref={videoInputRef}
                style={{display: "none"}}
                accept="video/*"
                onChange={handleVideoFileChange}
              />
              <input
                type="file"
                ref={documentInputRef}
                style={{display: "none"}}
                accept=".pdf"
                onChange={handleDocumentFileChange}
              />

              {/* Botones de elementos multimedia */}
              <div className="d-flex gap-2 flex-wrap mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleImageUpload}
                  disabled={!!comunicado.videoPreview}
                >
                  <FaImage className="me-1" /> Imagen
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleVideoUpload}
                  disabled={!!comunicado.imagePreview}
                >
                  <FaVideo className="me-1" /> Video
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onShowPollModal(index)}
                  disabled={
                    !!comunicado.imagePreview || !!comunicado.videoPreview
                  }
                >
                  <FaPoll className="me-1" /> Encuesta
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onShowLinkModal(index)}
                  disabled={
                    !!comunicado.imagePreview ||
                    !!comunicado.videoPreview ||
                    !!comunicado.poll
                  }
                >
                  <FaLink className="me-1" /> Enlace
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDocumentUpload}
                  disabled={
                    !!comunicado.imagePreview ||
                    !!comunicado.videoPreview ||
                    !!comunicado.poll
                  }
                >
                  <FaFilePdf className="me-1" /> Documento
                </Button>
              </div>
            </div>

            {/* Incentivo */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">
                <FaGift className="me-2" />
                Incentivo Asociado (Opcional)
              </Form.Label>
              <Form.Select
                value={comunicado.incentiveId || ""}
                onChange={(e) =>
                  updateField("incentiveId", e.target.value || null)
                }
              >
                <option value="">Sin incentivo</option>
                {incentives
                  .filter((i) => i.isActive)
                  .map((incentive) => (
                    <option key={incentive.id} value={incentive.id}>
                      {incentive.name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Columna de Vista Previa */}
          <Col md={4}>
            <Card className="bg-light sticky-top">
              <Card.Header className="py-2">
                <small className="fw-bold text-muted">
                  <FaWhatsapp className="me-2" style={{color: "#25D366"}} />
                  Vista Previa
                </small>
              </Card.Header>
              <Card.Body
                style={{
                  backgroundColor: "#E5DDD5",
                  minHeight: "400px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                {/* Burbuja de mensaje */}
                <div
                  className="rounded-3 shadow-sm ms-auto p-2"
                  style={{
                    maxWidth: "90%",
                    backgroundColor: "#DCF8C6",
                  }}
                >
                  {comunicado.title && (
                    <div className="fw-bold mb-1 text-primary small">
                      {comunicado.title}
                    </div>
                  )}

                  {/* Previews de adjuntos ahora DENTRO de la burbuja */}
                  {comunicado.imagePreview && (
                    <div className="mb-1">
                      <Image
                        src={comunicado.imagePreview}
                        fluid
                        rounded
                        style={{maxHeight: "150px"}}
                      />
                    </div>
                  )}

                  {comunicado.videoPreview && (
                    <div className="mb-1">
                      <video
                        src={comunicado.videoPreview}
                        muted
                        controls
                        style={{
                          maxHeight: "150px",
                          maxWidth: "100%",
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                  )}

                  {comunicado.poll && (
                    <div className="mb-1 p-2 bg-light rounded border">
                      <div className="fw-bold small">
                        <FaPoll className="me-1" />
                        {comunicado.poll.question}
                      </div>
                      <div className="small text-muted">
                        {comunicado.poll.options.length} opciones
                      </div>
                    </div>
                  )}

                  {comunicado.link && (
                    <div className="mb-1 p-2 bg-light rounded border">
                      <small className="text-primary d-block">
                        <FaLink className="me-1" />
                        Enlace
                      </small>
                      <small
                        className="text-muted"
                        style={{
                          wordBreak: "break-all",
                        }}
                      >
                        {comunicado.link}
                      </small>
                    </div>
                  )}

                  {comunicado.documentPreview && (
                    <div className="mb-1 p-2 bg-light rounded border">
                      <small className="text-danger d-block">
                        <FaFilePdf className="me-1" />
                        Documento PDF
                      </small>
                      <small
                        className="text-muted"
                        style={{
                          wordBreak: "break-all",
                        }}
                      >
                        {comunicado.documentPreview.name} (
                        {(comunicado.documentPreview.size / 1024).toFixed(1)}
                        KB)
                      </small>
                    </div>
                  )}

                  {comunicado.message ? (
                    <div className="small" style={{whiteSpace: "pre-wrap"}}>
                      {comunicado.message}
                    </div>
                  ) : (
                    <div className="small text-muted fst-italic">
                      Tu mensaje aparecerá aquí...
                    </div>
                  )}

                  {comunicado.incentiveId && (
                    <div className="mt-2">
                      <Badge bg="success" className="small">
                        <FaGift className="me-1" />
                        Incentivo incluido
                      </Badge>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CommunicationEditor;
